import { useEffect, useSyncExternalStore } from 'react'
import type { RatioThresholds } from '@bainner/banner-core'
import type { FigmaPluginLightboxMaster } from '../../components/layout/FigmaPluginLightbox.js'

/**
 * The shell/page seam for `FigmaPluginLightbox`'s "Master Added" state — Epic 49 seam-wiring
 * pass ("coordinates are not the model's job").
 *
 * THE PROBLEM: `FigmaPluginLightbox` is mounted in `WorkspaceAppShell.tsx`, a shell that sits
 * ABOVE `<Outlet/>` (`router.tsx`), while the campaign it needs to describe — `campaignId`,
 * `campaignName`, its imported masters, its ratio-group boundaries — is known only by
 * `WorkspacePage.tsx`, the page rendered INSIDE that outlet. React context cannot cross this
 * boundary: a `Provider` reaches its own DESCENDANTS, and `WorkspacePage` is the shell's
 * descendant, never its ancestor, so no context `WorkspacePage` provides can ever reach a
 * component the shell itself renders. `ElementSelectionContext.tsx`'s header names this exact
 * asymmetry when explaining why context works for IT (a page handing state down to its own
 * children) — this is the mirror-image case that context structurally cannot solve.
 *
 * `useOpenCampaignTabs.ts` already solved the identical shape of problem for the tab strip's
 * campaign name (see that file's own header, "Epic 48 Phase 02C lift: from per-component state
 * to a MODULE-LEVEL STORE") — this module is the same pattern, scoped to this one dialog:
 * `WorkspacePage` is the sole WRITER (`useRegisterFigmaPluginCampaign`, called unconditionally
 * on every render, mirroring `useRegisterCampaignTab`); `WorkspaceAppShell` is the sole READER
 * (`useFigmaPluginCampaign`, subscribed via `useSyncExternalStore` so a delete made INSIDE the
 * already-open dialog — which flows back through `onMastersChanged` into `WorkspacePage`'s own
 * refetch — reaches the dialog's master list live, not only the next time it's opened).
 *
 * Deliberately NOT persisted to `localStorage` (unlike `useOpenCampaignTabs.ts`): this is a live
 * mirror of whatever `WorkspacePage` currently holds, not a session preference. Surviving a
 * reload would mean briefly showing a PREVIOUS campaign's stale masters while the fresh
 * `WorkspacePage` mount re-fetches — worse than the honest `null` a reload actually starts from.
 *
 * `null` means exactly what `FigmaPluginLightbox`'s own `campaignId={null}` default means: no
 * campaign in scope, render the awaiting state. That is the value on every route `WorkspacePage`
 * never mounts on (everything except `/campaigns/:id`) — nothing here special-cases the route;
 * the store is simply never written to outside it, and its cleanup (below) clears it back to
 * `null` the moment `WorkspacePage` unmounts.
 *
 * ⚠ Honest limit, carried over from `FigmaPluginLightbox.tsx`'s own header: registering here does
 * NOT make the dialog live-update the instant the Figma plugin finishes an import mid-session —
 * it only removes the "the page already has this data but the dialog can't see it" gap. The
 * dialog still shows whatever `WorkspacePage`'s own last fetch returned, same as every other
 * surface on this page. `WorkspaceAppShell.tsx`'s own mount-site comment records the one
 * remaining reachability gap this store does not attempt to close: the "Figma Plugin" sidebar
 * row that opens this dialog is itself scoped OFF the `/campaigns/:id` route today (a separate,
 * already-accepted decision — see that file's "220px sidebar" header note), so on that exact
 * route this wiring currently has no click path in the shipped shell. It is still correct to
 * wire regardless: the moment a reachable trigger exists on that route again, this seam needs no
 * further changes.
 */
export interface FigmaPluginCampaignContext {
  campaignId: string
  campaignName: string | null
  masters: readonly FigmaPluginLightboxMaster[]
  ratioThresholds: RatioThresholds | undefined
  /** Forwarded straight to `FigmaPluginLightbox`'s own `onMastersChanged` — fired after a
   * per-master delete inside the dialog, so `WorkspacePage` bumps its `mastersVersion` and every
   * surface that lists masters (this store included, on the next render) picks up the change. */
  onMastersChanged: () => void
}

let current: FigmaPluginCampaignContext | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const cb of listeners) cb()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot(): FigmaPluginCampaignContext | null {
  return current
}

/** No real SSR in this Vite SPA — `null` ("no campaign in scope") is the honest pre-hydration
 * value, matching what a page that hasn't mounted yet would mean anyway. */
function getServerSnapshot(): FigmaPluginCampaignContext | null {
  return null
}

/** `WorkspaceAppShell`'s side — re-renders whenever `WorkspacePage` registers a new value. */
export function useFigmaPluginCampaign(): FigmaPluginCampaignContext | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * `WorkspacePage`'s side — registers `ctx` for as long as this component instance stays mounted
 * with it. On cleanup (a dependency change, or an unmount), clears the store back to `null` only
 * if nothing newer has since taken its place — the same guard shape prevents a stale unregister
 * from a just-superseded render clobbering a fresher one (relevant under React 18 Strict Mode's
 * mount→cleanup→re-mount double-invoke, and under a route change that re-renders this same page
 * instance with a new `id` before the previous effect's cleanup has run).
 *
 * Pass `null` while there's nothing to register (`id` not resolved yet, or unmounted) — the
 * awaiting state is `FigmaPluginLightbox`'s own honest default for that.
 */
export function useRegisterFigmaPluginCampaign(ctx: FigmaPluginCampaignContext | null): void {
  useEffect(() => {
    current = ctx
    notify()
    return () => {
      if (current === ctx) {
        current = null
        notify()
      }
    }
  }, [ctx])
}

/** Test-only: drops the in-memory value and every listener. Mirrors
 * `_resetOpenCampaignTabsForTest` in `useOpenCampaignTabs.ts`. */
export function _resetFigmaPluginCampaignForTest(): void {
  current = null
  listeners.clear()
}
